import React, { useState } from "react";
import styled from "styled-components";
import { colors2, SkeletonLoader, ButtonGhostCompact } from "oolib";
import { UI_BODY_SEMIBOLD_SM, UI_CAPTION_DF, UI_CAPTION_SEMIBOLD_DF } from "oolib/dist/v2/components/Typo2";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useGetInsightsQuickSummary } from "@/utils/react-query-hooks/aiAnnotation";
import { isFeatureDisabledOnProd } from "@/utils/isFeatureDisabledOnProd";
import { periodKey } from "./PrioritiesLayout/periodKeys";

/**
 * The three to five lines a PM reads first on a topic (or sub-topic): what
 * customers describe, then what the numbers say — written by the findings
 * agent's quick path from the same scope the panel shows (its period, its
 * compare period, every filter on), and cached on what it read. The quotes
 * each line rests on are checked on the server and kept with the summary;
 * the card shows the lines only. See okf-be ai/InsightsAgent/quick and
 * docs/quick-brief-explained.html.
 *
 *   nodeType  "topic" | "subtopic"
 *   nodeId
 *   scope     { activeFilters, topicClusteringLens, period, quickSummary: { compareKey } }
 *             — the drilldown scope, with the comparison key the page's
 *             period selection names
 *
 * All time reads the sample and the counts only (no leads, no momentum).
 * Nothing renders on prod while the feature is gated there.
 */
const Card = styled.div`
  background: ${colors2.white};
  border: 1px solid ${colors2.grey10};
  border-radius: 10px;
  padding: 14px 16px 12px;
  margin: 16px 0 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const Head = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;
// oolib's global reset strips list markers; this list wants them.
const List = styled.ul`
  margin: 0;
  padding-left: 20px;
  max-width: 700px;
  list-style: disc outside;
  display: flex;
  flex-direction: column;
  gap: 8px;
  & > li {
    list-style: disc outside;
    display: list-item;
  }
  & > li::marker {
    color: ${colors2.grey40};
  }
`;
export const TopicQuickSummary = ({ nodeType = "topic", nodeId, scope }) => {
  const platformConfigs = useGetQueryData("platformConfigs");
  const [force, setForce] = useState(false);
  const gated = isFeatureDisabledOnProd("insightsQuickSummary", {
    disabledFeatures: platformConfigs?.FEATURES_DISABLED_ON_PROD || [],
    isProd: import.meta.env.VITE_ENV === "prod",
  });
  // A period's key, or "all_time" when the picker says all time — the
  // server then reads the sample and the counts without leads or momentum.
  const key = periodKey(scope?.period) || (scope?.period ? "all_time" : null);
  const { data, isLoading, isFetching, error } = useGetInsightsQuickSummary({
    node: { type: nodeType, id: String(nodeId) },
    period: key,
    compareKey: scope?.quickSummary?.compareKey || null,
    activeFilters: scope?.activeFilters || [],
    topicClusteringLens: scope?.topicClusteringLens,
    force,
    enabled: !gated && !!key && !!nodeId,
  });
  if (gated || !key) return null;

  const loading = isLoading || (force && isFetching);
  const bullets = data?.bullets || [];
  const sample = data?.sample || null;
  // How many observations the writer actually read: the quotes picked per
  // section plus the one voice per smaller thread (the server says so as
  // `read`; older responses are summed here).
  const read = sample ? sample.read ?? (sample.sections || []).reduce((n, s) => n + (s.quotes || 0), 0) + (sample.tailQuotes || 0) : null;
  const footer = sample
    ? `Read ${read} of ${sample.population} observations${key === "all_time" ? ", all time" : ` in ${scope.period?.display || key}`}${data?.thin ? " · thin evidence" : ""}`
    : null;

  return (
    <Card>
      <Head>
        <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey60 }}>In short</UI_CAPTION_SEMIBOLD_DF>
        {data && !loading && <ButtonGhostCompact S icon="ArrowsClockwise" value="Regenerate" onClick={() => setForce(true)} />}
      </Head>
      {loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <SkeletonLoader style={{ height: "0.9rem", width: "92%", borderRadius: 4 }} />
          <SkeletonLoader style={{ height: "0.9rem", width: "80%", borderRadius: 4 }} />
          <SkeletonLoader style={{ height: "0.9rem", width: "60%", borderRadius: 4 }} />
          <UI_CAPTION_DF style={{ color: colors2.grey40 }}>Reading the feedback…</UI_CAPTION_DF>
        </div>
      )}
      {!loading && error && (
        <UI_CAPTION_DF style={{ color: colors2.grey60 }}>{error?.response?.data?.message || "Couldn't summarise this right now."}</UI_CAPTION_DF>
      )}
      {!loading && !error && data && (
        <>
          {bullets.length ? (
            <List>
              {bullets.map((b, i) => (
                <li key={i}>
                  <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey80 }}>{b.text}</UI_BODY_SEMIBOLD_SM>
                </li>
              ))}
            </List>
          ) : (
            <UI_CAPTION_DF style={{ color: colors2.grey60 }}>Nothing to say yet for this selection.</UI_CAPTION_DF>
          )}
          {footer && <UI_CAPTION_DF style={{ color: colors2.grey40, maxWidth: 700 }}>{footer}</UI_CAPTION_DF>}
        </>
      )}
    </Card>
  );
};
